// src/pages/finance-portal/invoices/InvoiceForm.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, CircularProgress, Autocomplete,
    MenuItem, Select, FormControl, InputLabel, Divider, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_TERMS = [
    { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
    { value: 'NET_15', label: 'Net 15' },
    { value: 'NET_30', label: 'Net 30' },
    { value: 'NET_60', label: 'Net 60' },
    { value: 'NET_90', label: 'Net 90' },
];

const ITEM_TYPES = [
    { value: 'SERVICE', label: 'Service' },
    { value: 'PRODUCT', label: 'Product' },
    { value: 'EXPENSE', label: 'Expense' },
    { value: 'DISCOUNT', label: 'Discount' },
];

const TERM_DAYS = {
    DUE_ON_RECEIPT: 0,
    NET_15: 15,
    NET_30: 30,
    NET_60: 60,
    NET_90: 90,
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function addDays(dateStr, days) {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
}

const emptyLine = {
    item_type: 'SERVICE',
    description: '',
    quantity: 1,
    unit_price: 0,
    tax_rate: '',
    discount_percent: '',
    display_order: 0,
};

export default function InvoiceForm() {
    const navigate = useNavigate();
    const { invoiceId } = useParams();
    const isEdit = Boolean(invoiceId);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Customer
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerLoading, setCustomerLoading] = useState(false);
    const customerTimer = useRef(null);

    // Template
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');

    // Invoice fields
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDateManual, setDueDateManual] = useState(false);
    const [paymentTerms, setPaymentTerms] = useState('NET_30');
    const [poNumber, setPoNumber] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [notes, setNotes] = useState('');
    const [terms, setTerms] = useState('');

    // Line items
    const [lines, setLines] = useState([{ ...emptyLine, display_order: 1 }]);

    // Invoice-level tax & discount
    const [taxRate, setTaxRate] = useState('');
    const [discountAmount, setDiscountAmount] = useState('');

    // Existing invoice data (for edit)
    const [existingInvoice, setExistingInvoice] = useState(null);

    // Fetch templates on mount
    useEffect(() => {
        axiosInstance.get('/api/finance/invoice-templates')
            .then(res => {
                if (res.data.success) setTemplates(res.data.data || []);
            })
            .catch(() => {});
    }, []);

    // Auto-calculate due date from payment terms
    useEffect(() => {
        if (!dueDateManual && invoiceDate && paymentTerms) {
            const days = TERM_DAYS[paymentTerms] || 0;
            setDueDate(addDays(invoiceDate, days));
        }
    }, [invoiceDate, paymentTerms, dueDateManual]);

    // Customer search with debounce
    useEffect(() => {
        if (customerTimer.current) clearTimeout(customerTimer.current);
        const params = { per_page: 50 };
        if (customerSearch && customerSearch.length >= 2) params.search = customerSearch;
        setCustomerLoading(true);
        customerTimer.current = setTimeout(async () => {
            try {
                const res = await axiosInstance.get('/api/finance/customers', { params });
                if (res.data.success) {
                    setCustomerOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to search customers', err);
            } finally {
                setCustomerLoading(false);
            }
        }, customerSearch ? 300 : 0);
        return () => { if (customerTimer.current) clearTimeout(customerTimer.current); };
    }, [customerSearch]);

    // Load existing invoice for edit
    useEffect(() => {
        if (isEdit) {
            setLoading(true);
            axiosInstance.get(`/api/finance/invoices/${invoiceId}`)
                .then(res => {
                    if (res.data.success) {
                        const inv = res.data.data;
                        setExistingInvoice(inv);
                        setInvoiceDate(inv.invoice_date || '');
                        setDueDate(inv.due_date || '');
                        setDueDateManual(true);
                        setPaymentTerms(inv.payment_terms || 'NET_30');
                        setPoNumber(inv.po_number || '');
                        setCurrency(inv.currency || 'USD');
                        setNotes(inv.notes || '');
                        setTerms(inv.terms || '');
                        setTaxRate(inv.tax_rate != null ? String(inv.tax_rate) : '');
                        setDiscountAmount(inv.discount_amount != null ? String(inv.discount_amount) : '');
                        setSelectedTemplate(inv.template_id || '');

                        if (inv.customer) {
                            setSelectedCustomer(inv.customer);
                            setCustomerOptions([inv.customer]);
                        }

                        if (inv.line_items && inv.line_items.length > 0) {
                            setLines(inv.line_items.map((li, idx) => ({
                                id: li.id,
                                item_type: li.item_type || 'SERVICE',
                                description: li.description || '',
                                quantity: li.quantity != null ? li.quantity : 1,
                                unit_price: li.unit_price != null ? li.unit_price : 0,
                                tax_rate: li.tax_rate != null ? String(li.tax_rate) : '',
                                discount_percent: li.discount_percent != null ? String(li.discount_percent) : '',
                                display_order: li.display_order != null ? li.display_order : idx + 1,
                            })));
                        }
                    }
                })
                .catch(err => {
                    setError(err.response?.data?.message || 'Failed to load invoice');
                })
                .finally(() => setLoading(false));
        }
    }, [isEdit, invoiceId]);

    const updateLine = (index, field, value) => {
        const updated = [...lines];
        updated[index] = { ...updated[index], [field]: value };
        setLines(updated);
    };

    const addLine = () => {
        const maxOrder = lines.reduce((max, l) => Math.max(max, l.display_order || 0), 0);
        setLines([...lines, { ...emptyLine, display_order: maxOrder + 1 }]);
    };

    const removeLine = (index) => {
        if (lines.length > 1) {
            setLines(lines.filter((_, i) => i !== index));
        }
    };

    const moveLineUp = (index) => {
        if (index === 0) return;
        const updated = [...lines];
        [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
        updated.forEach((l, i) => { l.display_order = i + 1; });
        setLines(updated);
    };

    const moveLineDown = (index) => {
        if (index >= lines.length - 1) return;
        const updated = [...lines];
        [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
        updated.forEach((l, i) => { l.display_order = i + 1; });
        setLines(updated);
    };

    // Calculations
    const lineAmount = (line) => {
        const base = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
        const discPct = parseFloat(line.discount_percent) || 0;
        return base - (base * discPct / 100);
    };

    const subtotal = lines.reduce((sum, l) => sum + lineAmount(l), 0);

    const lineTaxTotal = lines.reduce((sum, l) => {
        const amt = lineAmount(l);
        const rate = parseFloat(l.tax_rate) || 0;
        return sum + (amt * rate / 100);
    }, 0);

    const invoiceTaxAmount = subtotal * (parseFloat(taxRate) || 0) / 100;
    const totalTax = lineTaxTotal + invoiceTaxAmount;
    const totalDiscount = parseFloat(discountAmount) || 0;
    const total = subtotal + totalTax - totalDiscount;

    const buildPayload = () => {
        return {
            customer_id: selectedCustomer?.id || null,
            invoice_date: invoiceDate,
            due_date: dueDate,
            payment_terms: paymentTerms,
            po_number: poNumber,
            currency: currency,
            notes: notes,
            terms: terms,
            tax_rate: parseFloat(taxRate) || 0,
            discount_amount: parseFloat(discountAmount) || 0,
            template_id: selectedTemplate || null,
            line_items: lines.map((l, idx) => ({
                id: l.id || undefined,
                item_type: l.item_type,
                description: l.description,
                quantity: parseFloat(l.quantity) || 0,
                unit_price: parseFloat(l.unit_price) || 0,
                tax_rate: parseFloat(l.tax_rate) || 0,
                discount_percent: parseFloat(l.discount_percent) || 0,
                display_order: idx + 1,
            })),
        };
    };

    const handleSaveDraft = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = buildPayload();
            payload.status = 'DRAFT';

            if (isEdit) {
                await axiosInstance.put(`/api/finance/invoices/${invoiceId}`, payload);
                setSuccess('Invoice updated successfully');
            } else {
                const res = await axiosInstance.post('/api/finance/invoices', payload);
                if (res.data.success && res.data.data?.id) {
                    navigate(`/finance-portal/invoices/${res.data.data.id}`, { replace: true });
                    return;
                }
                setSuccess('Invoice created successfully');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save invoice');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAndSend = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = buildPayload();

            let savedInvoiceId = invoiceId;
            if (isEdit) {
                await axiosInstance.put(`/api/finance/invoices/${invoiceId}`, payload);
            } else {
                const res = await axiosInstance.post('/api/finance/invoices', payload);
                if (res.data.success && res.data.data?.id) {
                    savedInvoiceId = res.data.data.id;
                }
            }

            await axiosInstance.post(`/api/finance/invoices/${savedInvoiceId}/send`);
            navigate(`/finance-portal/invoices/${savedInvoiceId}`, { replace: true });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save and send invoice');
        } finally {
            setSaving(false);
        }
    };

    const canSendInvoice = !isEdit || (existingInvoice && (existingInvoice.status === 'DRAFT' || existingInvoice.status === 'SENT'));

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/invoices')} sx={{ color: 'white', mr: 0.5 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ReceiptLongIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                            {isEdit ? `Edit Invoice ${existingInvoice?.invoice_number || ''}` : 'New Invoice'}
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            {isEdit ? 'Update invoice details' : 'Create a new invoice'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Customer & Invoice Details */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Invoice Details</Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Autocomplete
                                size="small"
                                options={customerOptions}
                                getOptionLabel={opt => opt ? (opt.company_name || `${opt.first_name || ''} ${opt.last_name || ''}`.trim()) : ''}
                                value={selectedCustomer}
                                onChange={(_, val) => setSelectedCustomer(val)}
                                onInputChange={(_, val) => setCustomerSearch(val)}
                                loading={customerLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Customer"
                                        required
                                        InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { fontFamily, borderRadius: '10px' },
                                            endAdornment: (
                                                <>
                                                    {customerLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText="Type to search customers"
                            />
                            <FormControl size="small">
                                <InputLabel sx={{ fontFamily }}>Payment Terms</InputLabel>
                                <Select
                                    value={paymentTerms}
                                    onChange={e => { setPaymentTerms(e.target.value); setDueDateManual(false); }}
                                    label="Payment Terms"
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {PAYMENT_TERMS.map(pt => (
                                        <MenuItem key={pt.value} value={pt.value} sx={{ fontFamily }}>{pt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                            <TextField
                                size="small" type="date" label="Invoice Date" required
                                value={invoiceDate}
                                onChange={e => { setInvoiceDate(e.target.value); setDueDateManual(false); }}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <TextField
                                size="small" type="date" label="Due Date" required
                                value={dueDate}
                                onChange={e => { setDueDate(e.target.value); setDueDateManual(true); }}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <TextField
                                size="small" label="PO Number"
                                value={poNumber}
                                onChange={e => setPoNumber(e.target.value)}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <TextField
                                size="small" label="Currency"
                                value={currency}
                                onChange={e => setCurrency(e.target.value)}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                        </Box>

                        {templates.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel sx={{ fontFamily }}>Template</InputLabel>
                                    <Select
                                        value={selectedTemplate}
                                        onChange={e => setSelectedTemplate(e.target.value)}
                                        label="Template"
                                        sx={{ fontFamily, borderRadius: '10px' }}
                                    >
                                        <MenuItem value="" sx={{ fontFamily }}>None</MenuItem>
                                        {templates.map(t => (
                                            <MenuItem key={t.id} value={t.id} sx={{ fontFamily }}>{t.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Line Items */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Line Items</Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={addLine}
                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, fontWeight: 600 }}
                            >
                                Add Line Item
                            </Button>
                        </Box>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 30 }}>#</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 110 }}>Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Description</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 80 }}>Qty</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 110 }}>Unit Price</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 80 }}>Tax %</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 90 }}>Discount %</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 110 }}>Amount</TableCell>
                                        <TableCell sx={{ width: 80 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lines.map((line, index) => {
                                        const amt = lineAmount(line);
                                        return (
                                            <TableRow key={index} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8', verticalAlign: 'top', pt: 1.5 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                                        <Tooltip title="Move up">
                                                            <span>
                                                                <IconButton size="small" onClick={() => moveLineUp(index)} disabled={index === 0} sx={{ color: '#cbd5e1', p: 0.25 }}>
                                                                    <DragIndicatorIcon sx={{ fontSize: 14, transform: 'rotate(90deg)' }} />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>{index + 1}</Typography>
                                                        <Tooltip title="Move down">
                                                            <span>
                                                                <IconButton size="small" onClick={() => moveLineDown(index)} disabled={index >= lines.length - 1} sx={{ color: '#cbd5e1', p: 0.25 }}>
                                                                    <DragIndicatorIcon sx={{ fontSize: 14, transform: 'rotate(-90deg)' }} />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <Select
                                                        size="small"
                                                        value={line.item_type}
                                                        onChange={e => updateLine(index, 'item_type', e.target.value)}
                                                        sx={{ fontFamily, fontSize: '0.8rem', borderRadius: '8px', width: '100%' }}
                                                    >
                                                        {ITEM_TYPES.map(t => (
                                                            <MenuItem key={t.value} value={t.value} sx={{ fontFamily, fontSize: '0.8rem' }}>{t.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        maxRows={3}
                                                        placeholder="Description"
                                                        value={line.description}
                                                        onChange={e => updateLine(index, 'description', e.target.value)}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="1"
                                                        value={line.quantity}
                                                        onChange={e => updateLine(index, 'quantity', e.target.value)}
                                                        inputProps={{ min: 0, step: 'any' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={line.unit_price}
                                                        onChange={e => updateLine(index, 'unit_price', e.target.value)}
                                                        inputProps={{ min: 0, step: '0.01' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="0"
                                                        value={line.tax_rate}
                                                        onChange={e => updateLine(index, 'tax_rate', e.target.value)}
                                                        inputProps={{ min: 0, max: 100, step: '0.01' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="0"
                                                        value={line.discount_percent}
                                                        onChange={e => updateLine(index, 'discount_percent', e.target.value)}
                                                        inputProps={{ min: 0, max: 100, step: '0.01' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ verticalAlign: 'top', pt: 1.5 }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                        {formatCurrency(amt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeLine(index)}
                                                        disabled={lines.length <= 1}
                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addLine}
                            sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1.5, fontWeight: 600 }}
                        >
                            Add Line Item
                        </Button>
                    </CardContent>
                </Card>

                {/* Totals & Tax/Discount */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                    {/* Notes & Terms */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Notes & Terms</Typography>
                            <TextField
                                label="Notes"
                                size="small"
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Notes visible to the customer on the invoice"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Terms & Conditions"
                                size="small"
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Terms and conditions for this invoice"
                                value={terms}
                                onChange={e => setTerms(e.target.value)}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                        </CardContent>
                    </Card>

                    {/* Totals */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Summary</Typography>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField
                                    size="small"
                                    label="Invoice Tax Rate %"
                                    type="number"
                                    value={taxRate}
                                    onChange={e => setTaxRate(e.target.value)}
                                    inputProps={{ min: 0, max: 100, step: '0.01' }}
                                    InputLabelProps={{ sx: { fontFamily } }}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    size="small"
                                    label="Discount Amount"
                                    type="number"
                                    value={discountAmount}
                                    onChange={e => setDiscountAmount(e.target.value)}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    InputLabelProps={{ sx: { fontFamily } }}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                    sx={{ flex: 1 }}
                                />
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Subtotal</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(subtotal)}</Typography>
                                </Box>
                                {totalTax > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Tax</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(totalTax)}</Typography>
                                    </Box>
                                )}
                                {totalDiscount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Discount</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#ef4444' }}>-{formatCurrency(totalDiscount)}</Typography>
                                    </Box>
                                )}
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                                    <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>Total</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(total)}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                        onClick={() => navigate('/finance-portal/invoices')}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSaveDraft}
                        disabled={saving}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            borderColor: ACCENT, color: ACCENT,
                            '&:hover': { borderColor: '#16a34a', bgcolor: 'rgba(34,197,94,0.04)' },
                        }}
                    >
                        Save Draft
                    </Button>
                    {canSendInvoice && (
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                            onClick={handleSaveAndSend}
                            disabled={saving || !selectedCustomer}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                            }}
                        >
                            Save & Send
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
